import React, { useState, useEffect } from "react";

import { useNavigate, useParams } from "react-router-dom";

import LeftMenu from "../components/LeftMenu";
import PastEventsCarousel from "../components/PastEventsCarousel";
import UpcomingEventsCarousel from "../components/UpcomingEventsCarousel";
import { debugLog } from "../utils/debugLog";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import { sportsDbFetch } from "../utils/sportsDbCache";
import webPlayerStorage from "../utils/webPlayerStorage";

// Map league names to TheSportsDB league IDs and sports
const LEAGUE_CONFIG = {
  ufc: { id: "4443", sport: "Fighting", name: "UFC", searchTerm: "UFC" },
  bellator: {
    id: "4816",
    sport: "Fighting",
    name: "Bellator MMA",
    searchTerm: "Bellator",
  },
  "formula-1": {
    id: "4370",
    sport: "Motorsport",
    name: "Formula 1",
    searchTerm: "Formula 1",
  },
  f1: {
    id: "4370",
    sport: "Motorsport",
    name: "Formula 1",
    searchTerm: "Formula 1",
  },
  nascar: {
    id: "4413",
    sport: "Motorsport",
    name: "NASCAR Cup Series",
    searchTerm: "NASCAR",
  },
  indycar: {
    id: "4528",
    sport: "Motorsport",
    name: "IndyCar Series",
    searchTerm: "IndyCar",
  },
  motogp: {
    id: "4449",
    sport: "Motorsport",
    name: "MotoGP",
    searchTerm: "MotoGP",
  },
  boxing: { sport: "Boxing", name: "Boxing", searchTerm: "Boxing" },
  mma: { sport: "Fighting", name: "MMA", searchTerm: "MMA" },
  fighting: { sport: "Fighting", name: "Fighting", searchTerm: "UFC" },
  motorsport: {
    sport: "Motorsport",
    name: "Motorsport",
    searchTerm: "Formula 1",
  },
};

// Search terms to fetch for each sport category
const FIGHTING_SEARCHES = ["UFC", "Bellator"];
const MOTORSPORT_SEARCHES = ["Formula 1", "NASCAR", "IndyCar", "MotoGP"];

const WebPlayerLeagueEvents = () => {
  const navigate = useNavigate();
  const { leagueName } = useParams();

  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leagueInfo, setLeagueInfo] = useState(null);
  const [divisions, setDivisions] = useState([]);

  // Get playlist and profile from storage
  const playlist = webPlayerStorage.getPlaylist();
  const profile = webPlayerStorage.getProfile();

  const leagueKey = leagueName?.toLowerCase().replace(/\s+/g, "-");
  const config = LEAGUE_CONFIG[leagueKey] || {
    sport: "Fighting",
    name: leagueName,
  };

  useEffect(() => {
    const fetchLeagueEvents = async () => {
      setLoading(true);
      try {
        // Fetch league info if specific ID exists
        if (config.id) {
          const leagueData = await sportsDbFetch(
            `lookupleague.php?id=${config.id}`
          );

          if (leagueData?.leagues?.length > 0) {
            setLeagueInfo(leagueData.leagues[0]);
          }
        }

        // Fetch divisions/teams for Fighting leagues (UFC weight classes)
        if (config.sport === "Fighting" && config.searchTerm) {
          try {
            const teamsData = await sportsDbFetch(
              `search_all_teams.php?l=${encodeURIComponent(config.searchTerm)}`
            );

            if (teamsData?.teams) {
              setDivisions(teamsData.teams);
            }
          } catch (err) {
            console.error("Error fetching divisions:", err);
          }
        }

        // Determine which search terms to use based on sport
        let searchTerms = [];

        if (config.sport === "Fighting") {
          searchTerms = FIGHTING_SEARCHES;
        } else if (config.sport === "Motorsport") {
          searchTerms = MOTORSPORT_SEARCHES;
        } else if (config.searchTerm) {
          searchTerms = [config.searchTerm];
        }

        if (searchTerms.length === 0) {
          setUpcomingEvents([]);
          setPastEvents([]);

          return;
        }

        debugLog(
          `[LeagueEvents] Fetching events for ${config.name} using search terms:`,
          searchTerms
        );

        // Fetch events using search API
        const fetchSearchResults = async (index = 0) => {
          if (index >= searchTerms.length) return [];

          const searchTerm = searchTerms[index];
          let events = [];

          try {
            const searchData = await sportsDbFetch(
              `searchevents.php?e=${encodeURIComponent(searchTerm)}`
            );

            events = searchData?.event || [];
          } catch (err) {
            console.error(`Error searching for ${searchTerm}:`, err);
          }

          return [...events, ...(await fetchSearchResults(index + 1))];
        };
        const allEvents = await fetchSearchResults();

        // Filter and normalize events
        const now = new Date();

        const upcoming = allEvents
          .filter((event) => {
            const eventDate = new Date(event.strTimestamp || event.dateEvent);

            if (isNaN(eventDate.getTime())) return false;

            const isFuture = eventDate > now;
            const correctSport = event.strSport === config.sport;

            return isFuture && correctSport;
          })
          .map((event) => normalizeEvent(event))
          .sort(
            (a, b) =>
              new Date(a.strTimestamp || a.dateEvent) -
              new Date(b.strTimestamp || b.dateEvent)
          );

        const past = allEvents
          .filter((event) => {
            const eventDate = new Date(event.strTimestamp || event.dateEvent);

            if (isNaN(eventDate.getTime())) return false;

            const isPast = eventDate <= now;
            const correctSport = event.strSport === config.sport;

            return isPast && correctSport;
          })
          .map((event) => normalizeEvent(event))
          .sort(
            (a, b) =>
              new Date(b.strTimestamp || b.dateEvent) -
              new Date(a.strTimestamp || a.dateEvent)
          );

        debugLog(
          `[LeagueEvents] Filtered results - Upcoming: ${upcoming.length}, Past: ${past.length}`
        );

        setUpcomingEvents(upcoming);
        setPastEvents(past);
      } catch (error) {
        console.error("Error fetching league events:", error);
        setUpcomingEvents([]);
        setPastEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeagueEvents();
  }, [leagueName, config.id, config.sport, config.name, config.searchTerm]);

  // Normalize TheSportsDB event to match our expected format
  const normalizeEvent = (event) => {
    const backdropImage =
      event.strFanart ||
      event.strBanner ||
      event.strThumb ||
      event.strSquare ||
      event.strPoster;

    const posterImage =
      event.strThumb || event.strPoster || event.strSquare || event.strFanart;

    return {
      ...event,
      mediaId: event.idEvent,
      id: event.idEvent,
      idEvent: event.idEvent,
      name: event.strEvent,
      homeTeam: event.strHomeTeam,
      awayTeam: event.strAwayTeam,
      strHomeTeam: event.strHomeTeam,
      strAwayTeam: event.strAwayTeam,
      posterPath: posterImage,
      backdropPath: backdropImage,
      strThumb: backdropImage,
      strFanart: event.strFanart,
      strBanner: event.strBanner,
      timestamp: event.strTimestamp,
      strTimestamp: event.strTimestamp,
      dateEvent: event.dateEvent,
      strTime: event.strTime,
      venue: { name: event.strVenue },
      league: { name: event.strLeague },
    };
  };

  // Get backdrop from league info or first event
  const backdrop =
    leagueInfo?.strFanart ||
    leagueInfo?.strBanner ||
    upcomingEvents[0]?.backdropPath ||
    pastEvents[0]?.backdropPath;

  return (
    <div className="min-h-screen relative">
      {/* Full Page Backdrop */}
      {backdrop && (
        <div className="fixed inset-0 z-0">
          <img alt="" className="w-full h-full object-cover" src={backdrop} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/90 to-black/95" />
        </div>
      )}

      <LeftMenu playlist={playlist} profile={profile} />

      <div className="pl-20 pt-8 pb-12 relative z-10">
        <div className="max-w-7xl mx-auto px-8">
          {/* Back Button */}
          <button
            className="text-white/60 hover:text-white mb-6 flex items-center gap-2 transition-colors"
            onClick={() => navigate("/web-player/sports")}
          >
            <i className="fas fa-arrow-left" />
            Back to Sports
          </button>

          {/* League Header */}
          <div className="mb-12">
            <div className="relative bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden">
              <div className="relative p-8">
                <div className="flex items-center gap-6">
                  {leagueInfo?.strBadge && (
                    <img
                      alt={config.name}
                      className="w-24 h-24 md:w-32 md:h-32 object-contain drop-shadow-2xl"
                      src={leagueInfo.strBadge}
                    />
                  )}
                  <div>
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg">
                      {config.name}
                    </h1>
                    <div className="flex items-center gap-3">
                      <span className="inline-block bg-blue-600 text-white text-sm font-bold px-4 py-2 rounded-full">
                        {config.sport}
                      </span>
                      <span className="text-white/60">
                        {upcomingEvents.length} upcoming • {pastEvents.length}{" "}
                        past events
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Weight Divisions - Only for Fighting sports */}
          {config.sport === "Fighting" && divisions.length > 0 && (
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <i className="fas fa-trophy text-blue-400" />
                Weight Divisions
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {divisions.map((division) => (
                  <div
                    className="bg-white/5 backdrop-blur-md rounded-lg border border-white/10 hover:border-blue-500/50 transition-all duration-300 overflow-hidden cursor-pointer group"
                    key={division.idTeam}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate(
                        `/web-player/sports/team/${encodeURIComponent(division.strTeam)}`
                      )
                    }
                    onKeyDown={activateOnEnterOrSpace}
                  >
                    <div className="relative h-32 bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center">
                      {division.strTeamBadge ? (
                        <img
                          alt={division.strTeam}
                          className="w-20 h-20 object-contain group-hover:scale-110 transition-transform duration-300"
                          src={division.strTeamBadge}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <i className="fas fa-trophy text-5xl text-white/30 group-hover:text-white/50 transition-colors duration-300" />
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-bold text-sm text-center line-clamp-2">
                        {division.strTeam}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Events Section */}
          {upcomingEvents.length > 0 && (
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <i className="fas fa-calendar-alt text-blue-400" />
                Upcoming Events
                <span className="text-xl font-normal text-white/60">
                  ({upcomingEvents.length})
                </span>
              </h2>
              <UpcomingEventsCarousel events={upcomingEvents} />
            </div>
          )}

          {/* Past Events Section */}
          {pastEvents.length > 0 && (
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <i className="fas fa-history text-blue-400" />
                Past Events
                <span className="text-xl font-normal text-white/60">
                  ({pastEvents.length})
                </span>
              </h2>
              <PastEventsCarousel events={pastEvents} />
            </div>
          )}

          {/* Empty State */}
          {!loading &&
            upcomingEvents.length === 0 &&
            pastEvents.length === 0 && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 mb-6">
                  <i className="fas fa-calendar-xmark text-4xl text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  No Events Available
                </h3>
                <p className="text-white/60 mb-6">
                  There are no {config.name} events scheduled at this time.
                  Check back later for updates.
                </p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default WebPlayerLeagueEvents;
